import type { BaseProperty } from '@interfaces/expedia/property';

export type HotelFilterPolicy = {
  query?: string;
  priceRange: number[];
  ratings?: string[];
  amenities?: string[];
  isBundle: boolean;
  selectedTicketPrice: number;
  sortBy?: string;
  maxDistance?: number;
};

export function filterHotels(
  hotels: BaseProperty[],
  {
    query,
    priceRange,
    ratings,
    amenities,
    isBundle,
    selectedTicketPrice,
    maxDistance,
  }: Omit<HotelFilterPolicy, 'sortBy'>
): BaseProperty[] {
  const [min, max] = priceRange;
  const ratingFloor = ratings?.length
    ? Math.min(...ratings.map((rate) => parseInt(rate, 10)))
    : null;

  return hotels.filter((property) => {
    if (query && !property.name.toLowerCase().includes(query.toLowerCase())) {
      return false;
    }

    const basePrice = isBundle
      ? property.price + selectedTicketPrice
      : property.price;
    if (basePrice < min || basePrice > max) return false;

    if (
      maxDistance != null &&
      Number.isFinite(property.distance) &&
      property.distance > maxDistance
    ) {
      return false;
    }

    if (ratingFloor != null && property.rating < ratingFloor) return false;

    if (amenities?.length) {
      const haystack = property.amenities.toLowerCase();
      if (
        !amenities.every((feature) => haystack.includes(feature.toLowerCase()))
      ) {
        return false;
      }
    }

    return true;
  });
}

export function sortHotels(
  hotels: BaseProperty[],
  sortBy?: string
): BaseProperty[] {
  return [...hotels].sort((left, right) => {
    switch (sortBy) {
      case 'price-low-high':
        return left.price - right.price;
      case 'price-high-low':
        return right.price - left.price;
      case 'rating-low-high':
        return left.rating - right.rating;
      case 'rating-high-low':
        return right.rating - left.rating;
      default:
        return left.distance - right.distance;
    }
  });
}
